"use client";

/**
 * EstateMock — animated mock of the askFinz Estate property-comparison
 * surface for /apps/estate.
 *
 * Pure presentation: three property cards side-by-side, filter chips
 * along the top, a "diligence note" agent draft typing itself in below.
 * One card carries a brand-red "askFinz pick" corner badge.
 *
 * No estate.askfinz.ai product app exists yet (just a placeholder
 * page.tsx) — this is presentation-only. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Home,
  BedDouble,
  Bath,
  Ruler,
  Calendar,
  GraduationCap,
  Footprints,
  MapPin,
  Sparkles,
  FileSignature,
  ArrowUpRight,
} from "lucide-react";

type Property = {
  id: string;
  address: string;
  neighbourhood: string;
  price: string;
  priceNum: number;
  sqft: number;
  beds: number;
  baths: number;
  year: number;
  schoolScore: number;
  walkScore: number;
  weighted: number;
  pick?: boolean;
};

const PROPERTIES: Property[] = [
  {
    id: "p-1",
    address: "118 Linden Ave",
    neighbourhood: "Maple Heights",
    price: "$842k",
    priceNum: 842,
    sqft: 1840,
    beds: 3,
    baths: 2,
    year: 1998,
    schoolScore: 8,
    walkScore: 71,
    weighted: 76,
  },
  {
    id: "p-2",
    address: "220 Oak St",
    neighbourhood: "Cedar Park",
    price: "$915k",
    priceNum: 915,
    sqft: 2100,
    beds: 4,
    baths: 2.5,
    year: 2006,
    schoolScore: 9,
    walkScore: 64,
    weighted: 88,
    pick: true,
  },
  {
    id: "p-3",
    address: "47 Birchwood Dr",
    neighbourhood: "Westside",
    price: "$795k",
    priceNum: 795,
    sqft: 1620,
    beds: 3,
    baths: 1.5,
    year: 1972,
    schoolScore: 7,
    walkScore: 82,
    weighted: 69,
  },
];

const FILTERS = ["$700k–$950k", "3+ beds", "Cedar Park", "Top-rated schools", "Garage 2+", "Lot ≥ 0.2 acre"];

const NOTE_LINES = [
  "Property is in the Cedar Park district — recent comps 11% above asking.",
  "School zoning: Lincoln Elem (9/10) and Cedar Park High (8/10).",
  "Property tax basis last reset 2019 · 4 recent sales within 0.4 mi.",
  "Walkability moderate; nearest transit 0.6 mi · school bus pickup on-block.",
];

const CITED = [
  { label: "Tax assessor", id: "tax-2024" },
  { label: "MLS comps", id: "mls-recent" },
  { label: "School district", id: "schools-ne" },
];

// First-paint snapshot: filter chips already armed + property cards rendered
// (cards come from static PROPERTIES). The script adds the askFinz pick badge
// + types out the diligence-note lines.
const INITIAL_FILTERS = [...FILTERS];

export function EstateMock() {
  const [armedFilters, setArmedFilters] = useState<string[]>(INITIAL_FILTERS);
  const [highlightPick, setHighlightPick] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [typingLine, setTypingLine] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Loop reset = partial-fill (filters already on) — never the empty state.
      setArmedFilters(INITIAL_FILTERS);
      setHighlightPick(false);
      setLines([]);
      setTypingLine("");

      // 1. askFinz pick badge fades in (filters are already on from initial).
      schedule(() => setHighlightPick(true), 600);

      // 2. Diligence note types itself in
      const noteStart = 1100;
      NOTE_LINES.forEach((line, lineIdx) => {
        const lineStart = noteStart + lineIdx * 1400;
        const chunks = Math.min(line.length, 28);
        const step = Math.max(1, Math.floor(line.length / chunks));
        for (let k = 1; k <= chunks; k++) {
          schedule(
            () => setTypingLine(line.slice(0, Math.min(line.length, k * step))),
            lineStart + k * 38
          );
        }
        schedule(() => {
          setLines((cur) => [...cur, line]);
          setTypingLine("");
        }, lineStart + chunks * 38 + 80);
      });

      // Loop
      const totalMs = noteStart + NOTE_LINES.length * 1400 + 600 + 3500;
      schedule(runCycle, totalMs);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            estate.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-foreground/40" />
            3 of 28 listings
          </span>
        </div>

        {/* Filter strip */}
        <div className="flex items-center gap-2 flex-wrap border-b hairline px-4 py-3 bg-foreground/[0.02]">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono mr-1">
            Filters
          </span>
          {FILTERS.map((f) => {
            const on = armedFilters.includes(f);
            return (
              <span
                key={f}
                className={
                  "px-2.5 py-1 rounded-full border hairline text-[11px] transition-all duration-300 " +
                  (on
                    ? "bg-foreground text-background border-foreground"
                    : "text-foreground/65")
                }
              >
                {f}
              </span>
            );
          })}
          <span className="ml-auto text-[11px] text-foreground/65 inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Weighted by your priorities
          </span>
        </div>

        {/* Property grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-foreground/10">
          {PROPERTIES.map((p) => (
            <PropertyCard key={p.id} p={p} highlightPick={highlightPick} />
          ))}
        </div>

        {/* Diligence note */}
        <div className="border-t hairline bg-background px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <FileSignature className="h-3.5 w-3.5 text-foreground/65" />
            <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              Diligence note · 220 Oak St
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <span className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-pulse" />
              Drafting
            </span>
          </div>

          <div className="space-y-1.5 font-mono text-[12px] leading-relaxed text-foreground/80 min-h-[110px]">
            {lines.map((line, i) => (
              <div key={i} className="animate-[blur-fade-in_0.4s_ease-out]">
                <span className="text-foreground/35 mr-2 select-none">{(i + 1).toString().padStart(2, "0")}</span>
                {line}
              </div>
            ))}
            {typingLine && (
              <div>
                <span className="text-foreground/35 mr-2 select-none">
                  {(lines.length + 1).toString().padStart(2, "0")}
                </span>
                {typingLine}
                <span className="inline-block h-3 w-1 ml-0.5 bg-foreground/60 align-middle animate-pulse" />
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap text-[10px] text-foreground/65">
            <span className="tracking-[0.16em] uppercase">Cited:</span>
            {CITED.map((c) => (
              <span
                key={c.id}
                className="px-2 py-0.5 rounded-md border hairline bg-foreground/[0.03] font-mono"
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Coming soon to <span className="font-mono text-foreground/65">estate.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

function PropertyCard({ p, highlightPick }: { p: Property; highlightPick: boolean }) {
  return (
    <div className="relative bg-background p-4 min-h-[260px]">
      {/* askFinz pick — single brand-red accent in the mock */}
      {p.pick && highlightPick && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[9px] tracking-[0.16em] uppercase font-mono animate-[blur-fade-in_0.5s_ease-out]">
          <Sparkles className="h-2.5 w-2.5" />
          askFinz pick
        </span>
      )}

      <div className="flex items-center gap-2 text-foreground/65">
        <MapPin className="h-3.5 w-3.5" />
        <span className="text-[11px] tracking-[0.16em] uppercase font-mono">
          {p.neighbourhood}
        </span>
      </div>

      <div className="mt-1.5 font-medium tracking-tight text-[15px] text-foreground/90 flex items-center gap-1.5">
        <Home className="h-3.5 w-3.5 text-foreground/65" />
        {p.address}
      </div>

      <div className="mt-3 text-[22px] font-medium tracking-tight">{p.price}</div>

      <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-3 text-[11px]">
        <Stat icon={Ruler}    label="Sqft"     value={p.sqft.toLocaleString()} />
        <Stat icon={Calendar} label="Built"    value={String(p.year)} />
        <Stat icon={BedDouble} label="Beds"    value={String(p.beds)} />
        <Stat icon={Bath}     label="Baths"    value={String(p.baths)} />
        <Stat icon={GraduationCap} label="School" value={`${p.schoolScore}/10`} />
        <Stat icon={Footprints} label="Walk"   value={`${p.walkScore}`} />
      </div>

      {/* Weighted score bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
          <span>Weighted</span>
          <span className={p.pick ? "text-foreground/80" : "text-foreground/65"}>{p.weighted}</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
          <div
            className={"h-full transition-all duration-700 " + (p.pick ? "bg-foreground" : "bg-foreground/40")}
            style={{ width: `${p.weighted}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Home;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-foreground/65" />
      <span className="text-foreground/65">{label}</span>
      <span className="ml-auto text-foreground/85 font-mono">{value}</span>
    </div>
  );
}
